import React, { useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import AppDialog, {
  DialogCloseButton,
} from "../../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  closeDialog,
  openDialog,
} from "../../../components/dialog/dialogSlice";

import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCellProps,
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
} from "@progress/kendo-react-grid";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../../components/layout/Loading";
import IconButton from "../../../components/common/IconButton";
import { MdDelete } from "react-icons/md";
import AlertBox from "../../../components/common/AlertBox";
import { FaEdit } from "react-icons/fa";
import { deleteCall, getAllCalls } from "./services/calls.services";
import { clearCallsGUID, clearCallsDetails, setCallGUID } from "./callsSlice";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import CreateCallsDialog from "./CreateCallsDialog";
import { useResponsiveJSX } from "../../../_helper/helper";

const CallsDialog = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const gridRef = useRef<any>(null);
  const loading = useAppSelector((state) => state.calls.loading);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const CloseCallDialog: React.FC = () => {
    const CloseCall = () => {
      navigate(-1);
    };
    return (
      <div style={{ display: "flex", justifyContent: "end" }}>
        <Button
          style={{ width: "60px", padding: 4, marginTop: 10 }}
          themeColor={"primary"}
          fillMode={"outline"}
          onClick={CloseCall}
        >
          Back
        </Button>
      </div>
    );
  };
  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {dialogName === "CALL" && <CreateCallsDialog />}
      <DeleteCallDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Calls List
          </Typography.h4>
          <Button
            style={{ width: "auto", marginLeft: 5, padding: 6 }}
            themeColor={"primary"}
            type="button"
            fillMode="solid"
            size="small"
            title="Create Call"
            onClick={() => {
              dispatch(openDialog("CALL"));
              dispatch(clearCallsDetails());
              dispatch(clearCallsGUID());
            }}
          >
            Create Call
          </Button>
        </GridLayoutItem>
        <GridLayoutItem>
          <CallsGridComponent />
        </GridLayoutItem>
        <CloseCallDialog />
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}
const initialDataState: PageState = { skip: 0, take: 10 };
const CallsGridComponent: React.FC = () => {
  const dispatch = useAppDispatch();
  const breakPoint = useResponsiveJSX([1024, 2170, 2514]);
  const dynamicWidth = [150, 200, 250, 300][breakPoint] ?? 300;
  const CallsList = useAppSelector((state) => state.calls.CallsList);
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? CallsList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const ID = props.dataItem.CallGUID;
    const handleOpenDeleteDialog = () => {
      dispatch(openDialog("deleteCall"));
      dispatch(setCallGUID(ID));
    };
    const handleOpenEditDialog = () => {
      dispatch(setCallGUID(ID));
      // dispatch(getCallById(ID));
      dispatch(openDialog("CALL"));
    };
    return (
      <td className={`action-td ${props.className}`} style={{ ...props.style }}>
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Edit"
          onClick={handleOpenEditDialog}
        >
          <FaEdit className="absolute-position" style={{ fontSize: "20px" }} />
        </IconButton>
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Delete"
themeColor="error"
          onClick={handleOpenDeleteDialog}
        >
          <MdDelete
            className="absolute-position"
            style={{ fontSize: "24px" }}
          />
        </IconButton>
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      style={props.thProps?.style}
      className={`table-header ${
        props.index === 11 || props.index === 0 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td
        style={{ ...props.style }}
        {...props.tdProps}
        colSpan={1}
        className={`table-cell ${props.className}`}
      >
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );
  return (
    <>
      {CallsList && CallsList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
 className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(CallsList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : CallsList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={CallsList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="CallDate"
            title="Date"
            width={200}
            locked
            cell={(props: any) => (
              <td className={` ${props.className}`} style={{ ...props.style }}>
                {props.dataItem.CallDate
                  ? moment(props.dataItem.CallDate, "DD/MM/YYYY").format(
                      "DD/MM/YYYY"
                    )
                  : ""}
              </td>
            )}
          />
          <Column field="Subject" title="Subject" width={dynamicWidth} />
          <Column field="Agenda" title="Agenda" width={dynamicWidth} />
          <Column
            field="Description"
            title="Description"
            width={dynamicWidth}
          />
          <Column field="CallType" title="Type" width={dynamicWidth} />
          <Column field="CallPurpose" title="Purpose" width={dynamicWidth} />
          <Column field="CallResult" title="Result" width={dynamicWidth} />
          <Column field="CallStatus" title="Status" width={dynamicWidth} />
          <Column
            field="FollowupDate"
            title="Followup Date"
            width={dynamicWidth}
          />
          <Column
            field="FollowupTime"
            title="Followup Time"
            width={dynamicWidth}
          />
          <Column
            field="FollowupNotes"
            title="Followup Notes"
            width={dynamicWidth}
          />
          <Column
            field="CallGUID"
            title="Actions"
            locked
            cell={MyEditCommandCell}
            width={"110px"}
            filterable={false}
          />
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteCallDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const CallsGUID = useAppSelector((state) => state.calls?.CallGUID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const NBInquiryTypeID = location?.state?.ActivityLog?.NBInquiryTypeID;
  const NBLeadTypeID = location.state?.ActivityLog?.NBLeadTypeID;
  const LeadID = location?.state?.ActivityLog?.LeadID;
  const payload = {
    LeadID: LeadID ? LeadID : 0,
    NBInquiryTypeID: NBInquiryTypeID ? NBInquiryTypeID : 0,
    NBLeadTypeID: NBLeadTypeID ? NBLeadTypeID : 0,
  };
  const handleDeleteAction = async (ID: string | null) => {
    if (ID) {
      const response = await dispatch(deleteCall(ID));
      if (response.payload?.Data.Status === 200) {
        dispatch(getAllCalls(payload));
        dispatch(closeDialog());
        dispatch(clearCallsGUID());
      } else {
        dispatch(getAllCalls(payload));
        dispatch(closeDialog());
        dispatch(clearCallsGUID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteCall" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Call?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Call?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(CallsGUID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default CallsDialog;
